import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, IconButton, Grid, MenuItem, Menu, Typography } from '@mui/material';
import {
    Refresh,
    Restore,
    ArrowDropDown,
    DeviceHub,
    Cast,
    GridOn,
    ViewHeadline,
    ShoppingCart,
    Storage
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiStore } from '@core/store';
import { shopService, moduleService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { DeleteConfirmationDialog, LoadingSpinner } from '@/components';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';
import PageTitleBar from '@/components/shared/PageTitleBar.tsx';
import { getMenuData } from '@core/data/menuData.ts';
import WcnTable, { DataTableColumn, DataTableExpansion } from '@/components/datatable/WcnTable.tsx';
import BaseSelector from '@/components/selectors/BaseSelector.tsx';
import { DeviceModule, Shop } from '@core/types';

const deviceTypeOptions = [
    { id: 'all', name: 'all' },
    { id: 'TrolleyShelter', name: 'trolleyShelter' },
    { id: 'ZoneTracker', name: 'zoneTracker' },
    { id: 'LineTracker', name: 'lineTracker' },
    { id: 'ShopAutomation', name: 'shopAutomation' },
    { id: 'SmartShelf', name: 'smartShelf' }
];

const getModuleIcon = (kind: string | undefined) => {
    switch (kind) {
        case 'TrolleyShelter':
            return <Cast sx={{ transform: 'rotate(180deg)' }} />;
        case 'ZoneTracker':
            return <GridOn />;
        case 'LineTracker':
            return <ViewHeadline />;
        case 'ShopAutomation':
            return <ShoppingCart />;
        case 'SmartShelf':
            return <Storage />;
        case 'Gateway':
            return <DeviceHub />;
        default:
            return null;
    }
};

const Modules: FCSecurePage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { notifySuccess, notifyWarningResponse, notifyError } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [shop, setShop] = useState<Shop | null>(null);
    const [devicesAndModules, setDevicesAndModules] = useState<DeviceModule[]>([]);
    const [deviceTypeFilter, setDeviceTypeFilter] = useState<string | null>(null);
    const [shopMenuAnchor, setShopMenuAnchor] = useState<null | HTMLElement>(null);
    const { shopIds } = useApiStore();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [selectedModule, setSelectedModule] = useState<DeviceModule | null>(null);

    const translatedDeviceTypeOptions = useMemo(
        () =>
            deviceTypeOptions.map((type) => ({
                id: type.id,
                name: t(type.name)
            })),
        [t]
    );

    const devicesAndModulesFiltered = useMemo(() => {
        let filtered = devicesAndModules;
        if (deviceTypeFilter && deviceTypeFilter !== 'all') {
            filtered = devicesAndModules.filter((x) => x.kind === deviceTypeFilter);
        }
        return filtered.sort((a, b) => (a.deviceId || a.moduleId || '').localeCompare(b.deviceId || b.moduleId || ''));
    }, [devicesAndModules, deviceTypeFilter]);

    const load = useCallback(async (shopId: string) => {
        setLoading(true);
        try {
            const data = await shopService.loadShopRecycledModules(shopId);
            setShop(data);

            const processedModules: any[] = [];

            if (data.gateways) {
                for (const gateway of data.gateways) {
                    const recycledModules = (gateway.modules || []).filter((module: any) => module.deleteDate);

                    if (recycledModules.length > 0) {
                        // Add gateway as a row
                        processedModules.push({
                            id: gateway.deviceId,
                            moduleId: '',
                            deviceId: gateway.deviceId,
                            kind: 'Gateway',
                            shopId: shopId
                        });

                        // Add modules
                        for (const module of recycledModules) {
                            processedModules.push({
                                ...module,
                                kind: module.discriminator,
                                shopId: shopId,
                                deviceId: gateway.deviceId
                            });
                        }
                    }
                }
            }

            setDevicesAndModules(processedModules);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (id) {
            load(id);
        }
    }, [id, load]);

    const retrieveFromRecyclingBin = useCallback(async () => {
        if (!selectedModule) return;

        try {
            await moduleService.postModuleAction(
                selectedModule.kind as any,
                selectedModule.id as string,
                '/restore-from-recycle-bin'
            );
            notifySuccess();
            if (id) await load(id);
        } catch (error: any) {
            if (error?.response?.status === 409) {
                notifyWarningResponse(error);
            } else {
                console.error(error);
            }
        }
    }, [selectedModule, id, notifySuccess, notifyWarningResponse, load]);

    const handleRestoreModule = useCallback(async () => {
        if (!selectedModule) return;

        setRestoreDialogOpen(false);
        setLoading(true);

        try {
            // Validation - Check if parent shop is deleted before restoring module
            const parentShop = await shopService.loadShops().then((shops) => shops.find((s) => s.id === id));

            if (parentShop?.deleteDate) {
                notifyError(t('cannotRestoreModuleMessage', { item: shop?.name || '' }));
            } else {
                await retrieveFromRecyclingBin();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setSelectedModule(null);
        }
    }, [selectedModule, id, shop?.name, notifyError, t, retrieveFromRecyclingBin]);

    const deleteModuleFromRecyclingBin = useCallback(async () => {
        if (!selectedModule) return;

        try {
            await moduleService.deleteModule(selectedModule.kind as any, selectedModule.id as string);
            notifySuccess();
            if (id) await load(id);
        } catch (error) {
            console.error(error);
        }
    }, [selectedModule, id, notifySuccess, load]);

    const handleDeleteModule = useCallback(async () => {
        if (!selectedModule) return;

        setDeleteDialogOpen(false);
        setLoading(true);

        try {
            // Validation - Check if parent shop is deleted before deleting module
            const parentShop = await shopService.loadShops().then((shops) => shops.find((s) => s.id === id));

            if (parentShop?.deleteDate) {
                notifyError(t('cannotDeleteModuleMessage', { item: shop?.name || '' }));
            } else {
                await deleteModuleFromRecyclingBin();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setSelectedModule(null);
        }
    }, [selectedModule, id, shop?.name, notifyError, t, deleteModuleFromRecyclingBin]);

    const gatewayRows = useMemo(
        () => devicesAndModulesFiltered.filter((x) => x.kind === 'Gateway'),
        [devicesAndModulesFiltered]
    );

    const columns: DataTableColumn<DeviceModule>[] = [
        {
            text: '',
            value: 'icon',
            width: 60,
            sortable: false,
            render: (row) => getModuleIcon(row.kind)
        },
        {
            text: t('gateway'),
            value: 'deviceId'
        }
    ];

    const moduleColumns: DataTableColumn<DeviceModule>[] = [
        {
            text: '',
            value: 'icon',
            width: 60,
            sortable: false,
            render: (row) => getModuleIcon(row.kind)
        },
        {
            text: t('modules'),
            value: 'moduleId'
        },
        {
            text: t('deleteDate'),
            value: 'deleteDate',
            render: (row) => (row.deleteDate ? new Date(row.deleteDate).toLocaleDateString() : '')
        },
        {
            text: t('finalDeleteDate'),
            value: 'permanentDeleteDate',
            render: (row) => (row.permanentDeleteDate ? new Date(row.permanentDeleteDate).toLocaleDateString() : '')
        },
        {
            text: '',
            value: 'actions',
            width: 100,
            align: 'right',
            sortable: false,
            render: (row) => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedModule(row);
                            setRestoreDialogOpen(true);
                        }}
                        title={t('restore')}
                    >
                        <Restore fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedModule(row);
                            setDeleteDialogOpen(true);
                        }}
                        title={t('delete')}
                    >
                        <i className="bi bi-trash3" />
                    </IconButton>
                </Box>
            )
        }
    ];

    const expansion: DataTableExpansion<DeviceModule, DeviceModule> = {
        getRowKey: (item) => item.deviceId || item.id,
        showExpandIconCondition: (row) => row.kind === 'Gateway',
        nestedTable: {
            columns: moduleColumns,
            data: devicesAndModulesFiltered,
            getChildRows: (row, allData) => allData.filter((x) => x.deviceId === row.deviceId && x.kind !== 'Gateway'),
            size: 'small',
            noDataMessage: t('noData')
        }
    };

    return (
        <Box>
            <PageTitleBar
                t={t}
                menus={getMenuData()}
                titleSlot={<span>{t('modules')}</span>}
                currentRoute={{
                    path: `/administration/recyclebin/${id}/modules`,
                    meta: {
                        breadcrumb: {
                            breadcrumbInactive: t('recycleBin'),
                            breadcrumbActive: t('modules')
                        }
                    }
                }}
            >
                <Button
                    variant="text"
                    startIcon={<Refresh />}
                    onClick={() => id && load(id)}
                    color="inherit"
                    sx={{ textTransform: 'none' }}
                >
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                        {t('refresh')}
                    </Box>
                </Button>
            </PageTitleBar>
            <Box sx={{ width: '100%', px: 3, py: 3 }}>
                <Grid container spacing={3} sx={{ mb: 2, alignItems: 'flex-end' }}>
                    <Grid item xs={12} lg={3} md={4} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="h3" component="h3" sx={{ mb: 0, mr: 1, fontSize: '1.25rem' }}>
                            {shop?.name}
                        </Typography>
                        <IconButton size="small" onClick={(e) => setShopMenuAnchor(e.currentTarget)}>
                            <ArrowDropDown />
                        </IconButton>
                        <Menu
                            anchorEl={shopMenuAnchor}
                            open={Boolean(shopMenuAnchor)}
                            onClose={() => setShopMenuAnchor(null)}
                        >
                            {shopIds.map((shopItem) => (
                                <MenuItem
                                    key={shopItem.id}
                                    onClick={() => {
                                        navigate(`/manage-shop/recyclebin/${shopItem.id}/modules`);
                                        setShopMenuAnchor(null);
                                    }}
                                >
                                    {shopItem.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid>
                    <Box sx={{ flexGrow: 1 }} />
                    <Grid
                        item
                        xs={12}
                        lg={2}
                        md={3}
                        sm={4}
                        sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}
                    >
                        <Box
                            sx={{
                                '& .MuiSelect-select': {
                                    backgroundColor: 'transparent !important'
                                },
                                width: '100%'
                            }}
                        >
                            <BaseSelector
                                value={deviceTypeFilter ?? 'all'}
                                items={translatedDeviceTypeOptions}
                                itemText="name"
                                itemValue="id"
                                label={t('moduleType')}
                                onChange={(value) => setDeviceTypeFilter(value)}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {loading && <LoadingSpinner showBackground={false} />}

                {!loading && devicesAndModulesFiltered.length > 0 && (
                    <WcnTable
                        columns={columns}
                        data={gatewayRows}
                        expansion={expansion}
                        loading={loading}
                        noDataMessage={t('noData')}
                        size="small"
                    />
                )}
            </Box>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                heading={t('deleteHeading')}
                message={t('deleteModuleMessage', { item: selectedModule?.moduleId || '' })}
                onConfirm={handleDeleteModule}
                onCancel={() => {
                    setDeleteDialogOpen(false);
                    setSelectedModule(null);
                }}
            />

            <DeleteConfirmationDialog
                open={restoreDialogOpen}
                heading={t('restoreHeading')}
                message={t('restoreModuleMessage', { item: selectedModule?.moduleId || '' })}
                onConfirm={handleRestoreModule}
                onCancel={() => {
                    setRestoreDialogOpen(false);
                    setSelectedModule(null);
                }}
            />
        </Box>
    );
};

Modules.displayName = 'Modules';
export default Modules;
